import { useState, useEffect, useRef, useId } from 'react';
import { Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface RevealableSectionProps {
  label: string;
  revealedLabel?: string;
  defaultRevealed?: boolean;
  children: React.ReactNode;
}

export const RevealableSection = ({
  label,
  revealedLabel,
  defaultRevealed = false,
  children,
}: RevealableSectionProps) => {
  const [revealed, setRevealed] = useState(defaultRevealed);
  const userToggledRef = useRef(false);
  const contentId = useId();

  // Prop-to-state sync for late-arriving async data (e.g., API response reveals section).
  // Guarded by userToggledRef to respect explicit user interaction.
  useEffect(() => {
    if (defaultRevealed && !userToggledRef.current) {
      setRevealed(true); // eslint-disable-line react-hooks/set-state-in-effect -- intentional prop-to-state sync, guarded by userToggledRef
    }
  }, [defaultRevealed]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setRevealed(prev => !prev);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        onClick={handleToggle}
        startIcon={revealed ? <RemoveIcon /> : <AddIcon />}
        size="small"
        aria-expanded={revealed}
        aria-controls={revealed ? contentId : undefined}
      >
        {revealed ? (revealedLabel ?? label) : label}
      </Button>
      {revealed && (
        <Box id={contentId} sx={{ mt: 1 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};
